"use client";

import { useId, useState } from "react";

/**
 * A password input with a reveal toggle.
 *
 * ONE COMPONENT RATHER THAN A TOGGLE PER FORM. Signup has two password fields, reset has two and
 * login has one; inlining the same button five times is five places for the icon, the aria-label
 * or the padding to drift apart. It also keeps the revealed state LOCAL to each field, so showing
 * the password does not also reveal the confirm field — which is the whole point of a confirm.
 *
 * `type` is swapped rather than the value re-rendered, so the browser's own password manager still
 * recognises the field while hidden, and autofill keeps working.
 *
 * NOT PERSISTED ANYWHERE. The revealed state dies with the component: a password left visible
 * after a reload would be a worse default than the one this replaces.
 */
export function PasswordField({
  value,
  onChange,
  showLabel,
  hideLabel,
  autoComplete,
  onKeyDown,
}: {
  value: string;
  onChange: (v: string) => void;
  /** aria-label when hidden — localized by the caller, never hardcoded English. */
  showLabel: string;
  /** aria-label when visible. */
  hideLabel: string;
  autoComplete?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const [visible, setVisible] = useState(false);
  const id = useId();

  return (
    <div className="relative">
      <input
        id={id}
        className="input pr-10"
        type={visible ? "text" : "password"}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        // `tabIndex={-1}` deliberately: tabbing from the password field should reach the submit
        // button, not a decorative toggle. The control stays reachable by pointer and by screen
        // readers through its aria-label.
        tabIndex={-1}
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        onClick={() => setVisible((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-fg"
      >
        {/* THE ICON SHOWS THE STATE, NOT THE ACTION (owner 2026-09-04: "look like when we hide
            we show it"). Both conventions are in use — GitHub shows the action, iOS shows the
            state — and he reads the action one as backwards: a plain open eye beside a masked
            field looks like the password is already visible. So a CROSSED eye means hidden and an
            open eye means shown, matching what the characters are doing.

            THE ARIA-LABEL DELIBERATELY DOES NOT FOLLOW. A screen reader announces a button by
            what pressing it DOES, so it stays "Show password" while hidden. Flipping both would
            make the control describe itself twice and act on neither. */}
        {visible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

/* Inline SVG rather than an icon package: two glyphs do not justify a dependency, and the CSP on
   the published app blocks external stylesheets anyway. `currentColor` makes them follow the
   surrounding text colour in both themes. */
function EyeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
